import VaccinesIcon from "@mui/icons-material/Vaccines";
import {
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import React, { useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import UserHint from "../../../../../components/display/UserHint";
import InpatientStayVaccinationListing from "../InpatientStayVaccinationListing";
const vaccCategories = ["Other", "Rabies"];

export default function InpatientStayVaccinationsStep() {
  const { control } = useFormContext();
  const { t } = useTranslation();

  const [hasRabies, setHasRabies] = React.useState(false);
  const { fields, append, remove } = useFieldArray({
    control,
    name: "vaccines",
  });

  const [newVaccine, setNewVaccine] = useState({
    name: "",
    date: null,
    category: "Other",
  });

  React.useEffect(() => {
    const res = fields.some(
      (o) =>
        String(o.category).toUpperCase() === vaccCategories[1].toUpperCase(),
    );
    setHasRabies(res);
  }, [fields]);

  const handleAddVaccine = () => {
    if (!newVaccine.name) return;
    if (vaccCategories.category === vaccCategories[1]) {
      if (hasRabies) {
        return;
      }
    }
    append({ ...newVaccine });
    setNewVaccine({ name: "", date: null, category: vaccCategories[0] });
  };

  const handleRemoveVaccine = (index) => {
    remove(index);
  };

  return (
    <Card variant="elevation" elevation={2}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <VaccinesIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t("Vaccines")}
          </Typography>
        </Stack>
        <Stack spacing={2} mb={2}>
          <TextField
            fullWidth
            inputProps={{ maxLength: 120 }}
            label={t("Title")}
            value={newVaccine.name}
            onChange={(e) =>
              setNewVaccine({ ...newVaccine, name: e.target.value })
            }
          />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MobileDatePicker
              label={t("VaccinationDate")}
              value={newVaccine.date}
              onChange={(newValue) =>
                setNewVaccine({ ...newVaccine, date: newValue })
              }
              format="DD/MM/YYYY"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("Category")}</InputLabel>
              <Select
                value={newVaccine.category}
                disabled={hasRabies}
                label={t("Category")}
                onChange={(e) =>
                  setNewVaccine({
                    ...newVaccine,
                    category: e.target.value,
                  })
                }
                input={
                  <OutlinedInput
                    label={t("Category")}
                    startAdornment={
                      <InputAdornment position="start">
                        <UserHint
                          maxWidth="100%"
                          iconOnly
                          hint={t("OtherCategoryExplanation")}
                          sx={{ p: 0 }}
                        />
                      </InputAdornment>
                    }
                  />
                }
              >
                {vaccCategories.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {t(opt)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Stack>
              <Controller
                name="nextVaccineDate"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <MobileDatePicker
                    disablePast
                    label={t("NextVaccinationDate")}
                    value={value ?? null}
                    onChange={onChange}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />
              <UserHint
                maxWidth="100%"
                hint={t("NextVaccineDateExplanation")}
                sx={{ pt: 1 }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end" sx={{ my: 2 }}>
              <Button
                variant="contained"
                onClick={handleAddVaccine}
                disabled={
                  !newVaccine.name ||
                  (newVaccine.category === vaccCategories[1] && hasRabies)
                }
              >
                {t("Add")}
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            {fields.map((field, index) => (
              <InpatientStayVaccinationListing
                key={field.id}
                vaccine={field}
                index={index}
                handleRemoveVaccine={handleRemoveVaccine}
              />
            ))}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
